import type { ApprovedFormulaDefinition } from './approved-formulas';

export type ApprovedFormulaRegistry = Readonly<{
  list(): readonly ApprovedFormulaDefinition[];
  get(formulaId: string, version: string): ApprovedFormulaDefinition;
  has(formulaId: string, version: string): boolean;
}>;

/**
 * Keeps the compiled registry lookup mechanics separate from the very large
 * catalog of definitions. Formula registration remains canonical in the
 * catalog; this helper owns no formula definitions.
 */
export function createApprovedFormulaRegistry(
  definitions: readonly ApprovedFormulaDefinition[],
): ApprovedFormulaRegistry {
  const byKey = new Map<string, ApprovedFormulaDefinition>(
    definitions.map((definition) => [key(definition.formulaId, definition.version), definition]),
  );
  return Object.freeze({
    list: () => definitions,
    get: (formulaId: string, version: string) => {
      const definition = byKey.get(key(formulaId, version));
      if (!definition) throw new Error(`Approved formula definition not found: ${formulaId}@${version}`);
      return definition;
    },
    has: (formulaId: string, version: string) => byKey.has(key(formulaId, version)),
  });
}

function key(formulaId: string, version: string): string {
  return `${formulaId}@${version}`;
}
